import React from 'react';
import Card from './Card';
import CardActionArea from './CardActionArea';
import CardMedia from './CardMedia';
import CardContent from './CardContent';
import Title from './Title';
import T from 'i18n-react';
import Term from './Term';
import Image from './Image';
import RenderLazyLoad from '../../../components/LazyLoad/RenderLazyLoad';
import Review from '../../../Businesses/BusinessCard/components/Review';
import NoImage from '../../../../../images/no-image.png';
import Small from './Small';
import { UsersitePlansGetManyPlansTypePayload } from '~/models/schema';
import Tag from './Tag';

/* UsersitePlansGetManyPlansTypePayloadから'id', 'favoriteCount', 'isFavorite'を除外した型チェック */
type RequiredPlanCardProps = Pick<
  UsersitePlansGetManyPlansTypePayload,
  'title'
>;
type NotRequiredPlanCardProps = Partial<
  Pick<
    UsersitePlansGetManyPlansTypePayload,
    | 'city'
    | 'cityKana'
    | 'thumbnailUrl'
    | 'regionName'
    | 'review'
    | 'active'
    | 'statusText'
    | 'futureOpen'
    | 'tagName'
    | 'url'
  >
>;

export type PlanCardProps = RequiredPlanCardProps &
  NotRequiredPlanCardProps & {
    defaultIsVisible?: boolean;
    onClick?: React.MouseEventHandler;
    fixTermTextLine?: boolean;
    mediaButton?: React.ReactNode;
    className?: string;
  };

const PlanCard = ({
  title,
  defaultIsVisible,
  url,
  onClick,
  thumbnailUrl,
  regionName,
  city,
  cityKana,
  statusText,
  tagName,
  active,
  fixTermTextLine,
  className,
  review,
  mediaButton,
}: PlanCardProps) => {
  const averageRating = review
    ? Math.floor(review.averageRating * 10) / 10
    : null;

  const handleClick = onClick
    ? (event: React.MouseEvent) => {
        event.preventDefault();

        onClick(event);

        window.location.href = url || '';
      }
    : undefined;

  const href = onClick ? undefined : url;

  return (
    <Card className={className || ''}>
      <CardActionArea onClick={handleClick} href={href}>
        <CardMedia title={title}>
          <RenderLazyLoad
            defaultIsVisible={defaultIsVisible}
            observerOptions={{ rootMargin: '200px' }}
            render={({ isVisible }) => (
              <Image
                isVisible={isVisible}
                src={isVisible ? thumbnailUrl || NoImage : null}
                alt="plan image"
                variant="rounded"
                loaded
                className="plan-image"
                disableFallback
              />
            )}
          />
          {mediaButton}
        </CardMedia>
        <CardContent>
          {review != null ? (
            <Review
              rating={averageRating}
              totalCount={review.totalCount.toLocaleString()}
              gutterBottom
            />
          ) : (
            <Small gutterBottom className="fixed-line-truncate-1" />
          )}
          <Title color="textPrimary" className="text-break-all" gutterBottom>
            {title}
          </Title>
          <Small
            className="line-truncate-1 text-break-all"
            gutterBottom
            color="primary"
          >
            <T.span
              text={{
                key: 'business.address.short',
                region: regionName,
                city,
              }}
            />
            {cityKana && (
              <T.span text={{ key: 'business.address.kana', kana: cityKana }} />
            )}
          </Small>
          <Term active={active} fixed={fixTermTextLine}>
            {statusText}
          </Term>
        </CardContent>
        {tagName != null && <Tag tagName={tagName} />}
      </CardActionArea>
    </Card>
  );
};

export default PlanCard;
